import { supabase } from '../lib/supabase';
import { PostgrestError } from '@supabase/supabase-js';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../hooks/useDomainQuery';

export interface Product {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  content: string | null;
  price: number | null;
  currency: string;
  stock_quantity: number;
  is_unique: boolean;
  status: 'available' | 'reserved' | 'sold';
  category_id: number | null;
  dimensions: string | null;
  materials: string[] | null;
  year: number | null;
  weight_kg: number | null;
  created_at: string;
  category?: Category;
  images?: ProductImage[];
}

export interface Category {
  id: number;
  name: string;
  slug: string;
  description: string | null;
}

export interface ProductImage {
  id: number;
  product_id: number;
  image_url: string;
  alt_text: string | null;
  is_featured: boolean;
}

export interface CustomerInfo {
  name: string;
  email: string;
  phone?: string;
  message?: string;
  shipping_address?: {
    street: string;
    city: string;
    postal_code: string;
    country: string;
  };
  billing_address?: {
    street: string;
    city: string;
    postal_code: string;
    country: string;
  };
}

export interface CartItem {
  product_id: number;
  quantity: number;
  price: number;
  title: string;
  image_url?: string;
}

export interface Order {
  id: number;
  order_number: string;
  customer_name: string;
  customer_email: string;
  total_amount: number;
  shipping_cost: number | null;
  status: 'pending_payment' | 'paid' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'inquiry';
  created_at: string;
}

// --- Fonctions Publiques (lecture) ---

/**
 * Récupère tous les produits disponibles avec filtres optionnels
 */
export async function getAvailableProducts({ 
  categorySlug = null,
  sortBy = 'created_at',
  sortOrder = 'desc',
  limit = 12,
  offset = 0
}: {
  categorySlug?: string | null;
  sortBy?: 'created_at' | 'price' | 'title';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}) {
  try {
    // Utiliser le filtrage par domaine avec fallback sur tenant_id
    let query = supabase
      .from('shop_products')
      .select(`
        *,
        category:category_id(id, name, slug),
        images:shop_product_images(*)
      `)
      .eq('status', 'available')
      .order(sortBy, { ascending: sortOrder === 'asc' });
    
    // Appliquer le filtre domaine
    query = applyDomainFilterToQuery(query)
      .limit(limit)
      .range(offset, offset + limit - 1);

    // Filtrer par catégorie si spécifié
    if (categorySlug) {
      const { data: category } = await supabase
        .from('shop_categories')
        .select('id')
        .eq('slug', categorySlug)
        .single();

      if (category) {
        query = query.eq('category_id', category.id);
      }
    }

    const { data, error, count } = await query;

    if (error) throw error;

    return { 
      products: data as Product[],
      count
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des produits:', error);
    throw error;
  }
}

/**
 * Récupère un produit par son slug
 */
export async function getProductBySlug(slug: string) {
  try {
    let query = supabase
      .from('shop_products')
      .select(`
        *,
        category:category_id(id, name, slug),
        images:shop_product_images(*)
      `)
      .eq('slug', slug)
      .eq('status', 'available');
    
    // Appliquer le filtre domaine
    query = applyDomainFilterToQuery(query);
    
    const { data, error } = await query.single();

    if (error) throw error;

    return data as Product;
  } catch (error) {
    console.error(`Erreur lors de la récupération du produit ${slug}:`, error);
    throw error;
  }
}

/**
 * Récupère toutes les catégories de produits
 */
export async function getProductCategories() {
  try {
    // Récupérer d'abord les produits du domaine pour filtrer les catégories
    let productsQuery = supabase
      .from('shop_products')
      .select('category_id')
      .eq('status', 'available');
    
    // Appliquer le filtre domaine
    productsQuery = applyDomainFilterToQuery(productsQuery);
    
    const { data: products, error: productsError } = await productsQuery;

    if (productsError) {
      console.error('Erreur lors de la récupération des produits:', productsError);
      throw productsError;
    }
    
    // Si aucun produit n'existe pour ce tenant, retourner un tableau vide
    if (!products || products.length === 0) {
      return [];
    }
    
    // Récupérer les IDs des catégories uniques
    const categoryIds = [...new Set(products.map(p => p.category_id).filter(Boolean))];
    
    // Si aucune catégorie n'est associée aux produits, retourner un tableau vide
    if (categoryIds.length === 0) {
      return [];
    }
    
    // Récupérer les catégories correspondantes
    const { data, error } = await supabase
      .from('shop_categories')
      .select('*')
      .in('id', categoryIds)
      .order('name');

    if (error) throw error;

    return data as Category[];
  } catch (error) {
    console.error('Erreur lors de la récupération des catégories:', error);
    throw error;
  }
}

/**
 * Récupère le nombre de produits par catégorie
 */
export async function getCategoriesWithCount() {
  try {
    // Récupérer d'abord les produits du domaine
    let productsQuery = supabase
      .from('shop_products')
      .select('category_id')
      .eq('status', 'available');
    
    // Appliquer le filtre domaine
    productsQuery = applyDomainFilterToQuery(productsQuery);
    
    const { data: products, error: productsError } = await productsQuery;
    
    if (productsError) {
      console.error('Erreur lors de la récupération des produits:', productsError);
      throw productsError;
    }
    
    // Si aucun produit n'existe pour ce tenant, retourner un tableau vide
    if (!products || products.length === 0) {
      return [];
    }
    
    // Récupérer les IDs des catégories uniques
    const categoryIds = [...new Set(products.map(p => p.category_id).filter(Boolean))];
    
    // Si aucune catégorie n'est associée aux produits, retourner un tableau vide
    if (categoryIds.length === 0) {
      return [];
    }
    
    // Récupérer les catégories
    const { data: categories, error } = await supabase
      .from('shop_categories')
      .select('*')
      .in('id', categoryIds);
      
    if (error) throw error;
    
    // Calculer manuellement le nombre de produits par catégorie
    const categoriesWithCount = categories.map(category => {
      const count = products.filter(p => p.category_id === category.id).length;
      return {
        ...category,
        product_count: count
      };
    });
    
    return categoriesWithCount;
  } catch (error) {
    console.error('Erreur lors de la récupération des catégories avec comptage:', error);
    throw error;
  }
}

// --- Fonctions Protégées (écriture, nécessitent une logique de panier/session) ---

/**
 * Crée une commande "Inquiry" (Demande d'information) pour une œuvre unique
 */
export async function createInquiryOrder(productId: number, customerInfo: CustomerInfo) {
  try {
    // Récupérer les informations du produit
    const { data: product, error: productError } = await supabase
      .from('shop_products')
      .select('*')
      .eq('id', productId)
      .single();

    if (productError) throw productError;

    // Créer la commande avec statut "inquiry"
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;
    
    const orderData = {
      customer_name: customerInfo.name,
      customer_email: customerInfo.email,
      customer_id: userId, // Utilisateur connecté ou null
      total_amount: product.price || 0, // Prix ou 0 si "Prix sur demande"
      status: 'inquiry' as const,
      notes: customerInfo.message || 'Demande d\'information',
      shipping_address: customerInfo.shipping_address ? JSON.stringify(customerInfo.shipping_address) : null,
      billing_address: customerInfo.billing_address ? JSON.stringify(customerInfo.billing_address) : null
    };
    
    const { data: order, error: orderError } = await supabase
      .from('shop_orders')
      .insert(orderData)
      .select()
      .single();

    if (orderError) throw orderError;

    // Créer l'élément de commande
    const { error: itemError } = await supabase
      .from('shop_order_items')
      .insert({
        order_id: order.id,
        product_id: productId,
        quantity: 1,
        price_at_purchase: product.price || 0
      });

    if (itemError) throw itemError;

    // Déclencher la fonction Edge pour envoyer les emails
    await fetch('/api/shop/inquiry', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orderId: order.id,
        customerInfo,
        productId
      }),
    });

    return order;
  } catch (error) {
    console.error('Erreur lors de la création de la demande d\'information:', error);
    throw error;
  }
}

/**
 * Crée une commande standard (pour les éditions/livres)
 */
export async function createStandardOrder(cartItems: CartItem[], customerInfo: CustomerInfo) {
  try {
    // Vérifier le stock avant de créer la commande
    for (const item of cartItems) {
      const { data: product, error } = await supabase
        .from('shop_products')
        .select('stock_quantity')
        .eq('id', item.product_id)
        .single();

      if (error) throw error;

      if (product.stock_quantity < item.quantity) {
        throw new Error(`Stock insuffisant pour le produit ${item.title}`);
      }
    }

    // Calculer le montant total
    const totalAmount = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    // Récupérer l'ID de l'utilisateur connecté
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;
    
    // Créer la commande
    const { data: order, error: orderError } = await supabase
      .from('shop_orders')
      .insert({
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_id: userId, // Utilisateur connecté ou null
        total_amount: totalAmount,
        status: 'pending_payment',
        shipping_address: customerInfo.shipping_address ? JSON.stringify(customerInfo.shipping_address) : null,
        billing_address: customerInfo.billing_address ? JSON.stringify(customerInfo.billing_address) : null
      })
      .select()
      .single();

    if (orderError) throw orderError;

    // Créer les éléments de commande
    for (const item of cartItems) {
      const { error: itemError } = await supabase
        .from('shop_order_items')
        .insert({
          order_id: order.id,
          product_id: item.product_id,
          quantity: item.quantity,
          price_at_purchase: item.price
        });

      if (itemError) throw itemError;
    }

    // Appeler la fonction Edge pour initialiser le paiement
    const response = await fetch('/api/shop/order', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orderId: order.id,
        customerInfo,
        cartItems
      }),
    });

    const paymentData = await response.json();

    return {
      order,
      paymentData
    };
  } catch (error) {
    console.error('Erreur lors de la création de la commande standard:', error);
    throw error;
  }
}

// Fonction utilitaire pour générer un slug unique à partir d'un titre
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Supprimer les caractères spéciaux
    .replace(/\s+/g, '-') // Remplacer les espaces par des tirets
    .replace(/-+/g, '-') // Éviter les tirets multiples
    .trim();
}
